/**
 * DOCTOR-WORKSPACE-COMPLETION-V1 -- the DOCTOR PATIENT SEARCH tool.
 *
 * DOCTOR-HOME-STATE-MODEL-V1 (F5, founder order 2026-08-14): the doctor's
 * lawful way to reach any patient in their clinic scope by MOBILE / UMR /
 * NAME -- the front-desk posture. A mobile query resolves through the
 * ADR-0004 fingerprint index inside THE ONE search core; the result cards
 * carry NO contact value in any mode (structurally absent -- stricter than
 * masking; the contact DISPLAY gap, gap.doctor-masked-contact, stays open).
 * A result row opens the DOCTOR LENS (a doctor-shaped Patient 360), never
 * the Patient Master record. The doctor does NOT register patients (that is
 * a front-desk action), so a no-match state offers the honest front-desk
 * route, never a "Register patient" control.
 *
 * SHIPPED-WORK-TRUTH-CORRECTIVE-V1 (T2, founder ruling 2026-08-15): the F5
 * HOME mount of this tool is RETIRED -- the doctor Home now carries the
 * front-desk-placed global search bar at the TOP, opening THE ONE
 * GlobalPatientSearchModal in its doctor context (same door, same
 * placement, same room as the front desk). This tool remains ONLY as the
 * legacy Patients-page embed (DoctorPatientsPage) and the standalone page
 * shape; the body stays factored (`DoctorSearchBody`, no PageHeader).
 *
 * SECURITY: the backend is the boundary (requireDoctorWorkspaceScope + org +
 * clinic fence). Candidate rows carry demographics only -- no phone, ever.
 */

import { useState } from 'react';
import { clinicLabelOf } from '@/dashboard/clinic-context';
import { Link, useNavigate } from 'react-router-dom';

import {
  fetchDoctorPatientSearch,
  type DoctorPatientSearchResult,
} from '@/dashboard/api';
import { PageHeader, SectionShell, EmptyState } from '@/dashboard/ui';
import { genderAgeLabel } from '@/dashboard/patients/patient-master-view';
import {
  doctorLensHref,
  DOCTOR_SEARCH_HINT,
  DOCTOR_SEARCH_NO_MATCH_BODY,
  DOCTOR_SEARCH_NO_MATCH_TITLE,
} from '@/dashboard/doctor/doctor-patient-lens-view';

interface SearchBodyProps {
  readonly query: string;
  readonly onQueryChange?: (q: string) => void;
  readonly onSubmit?: () => void;
  readonly loading: boolean;
  readonly error: string | null;
  readonly result: DoctorPatientSearchResult | null;
}

/**
 * The search BODY (SectionShell + form + results), WITHOUT a PageHeader -- so it
 * is the whole search page (DoctorPatientSearchContent) OR a demoted secondary
 * tool on the doctor Patients page. Pure presentational (no fetch).
 */
export function DoctorSearchBody({
  query,
  onQueryChange,
  onSubmit,
  loading,
  error,
  result,
}: SearchBodyProps): JSX.Element {
  return (
    // SHIPPED-WORK-TRUTH-CORRECTIVE-V1 (T2, founder ruling 2026-08-15):
    // the "Doctor lens" eyebrow is DEAD as user-facing copy -- machine
    // jargon never labels an operator surface. The section title carries
    // the job on its own.
    <SectionShell
      title="Search patients"
      sub={DOCTOR_SEARCH_HINT}
      testId="doctor-patient-search-section"
    >
      <form
        className="mt-3 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit?.();
        }}
      >
        <input
          type="search"
          name="doctor-patient-search"
          aria-label="Search patients by mobile, UMR, or name"
          placeholder="Mobile / UMR / name"
          value={query}
          autoComplete="off"
          className="w-full max-w-md rounded-md border border-border bg-background px-3 py-2 text-sm"
          data-testid="doctor-patient-search-input"
          onChange={(e) => onQueryChange?.(e.target.value)}
        />
        <button
          type="submit"
          disabled={loading || query.trim().length < 2}
          className="rounded-md border border-border bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          data-testid="doctor-patient-search-submit"
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {error ? (
        <p className="mt-3 text-sm text-destructive" role="alert" data-testid="doctor-patient-search-error">
          {error}
        </p>
      ) : null}

      {result !== null && !loading && !error ? (
        <DoctorSearchResults result={result} />
      ) : null}
    </SectionShell>
  );
}

/** Pure presentational full-page body (PageHeader + the search body). Retained
 *  as the standalone-page shape (and the unit-test entry point). */
export function DoctorPatientSearchContent(props: SearchBodyProps): JSX.Element {
  return (
    <div className="doctor-portal">
      <PageHeader
        title="Patients"
        subs={['Find any patient in your clinic to review care and continue treatment.']}
      />
      <DoctorSearchBody {...props} />
    </div>
  );
}

function DoctorSearchResults({
  result,
}: {
  result: DoctorPatientSearchResult;
}): JSX.Element {
  if (result.count === 0) {
    // T2: the ONE copy home (doctor-patient-lens-view) -- the shared search
    // door's doctor context renders the SAME pair.
    return (
      <EmptyState
        testId="doctor-search-empty"
        title={DOCTOR_SEARCH_NO_MATCH_TITLE}
        body={DOCTOR_SEARCH_NO_MATCH_BODY}
      />
    );
  }
  return (
    <div className="mt-4" data-testid="doctor-search-results">
      <p className="text-sm text-muted-foreground">
        {result.count} match{result.count === 1 ? '' : 'es'} found.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table className="table mt-2" data-testid="doctor-search-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Gender / age</th>
              <th>Registered at</th>
              <th>UMR</th>
              <th aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {result.candidates.map((c) => (
              <tr key={c.patientId} data-testid="doctor-search-row">
                <td className="font-medium text-foreground">
                  {c.fullName}
                  {c.status === 'inactive' ? (
                    <span className="ml-2 text-xs text-muted-foreground">(inactive)</span>
                  ) : null}
                </td>
                <td>{genderAgeLabel(c.gender, c.ageYears, c.ageIsApproximate)}</td>
                <td>
                  {c.registeredClinicLabel ??
                    (c.registeredClinicId !== null ? clinicLabelOf(null) : '-')}
                </td>
                <td className="font-mono text-xs">{c.umr}</td>
                <td>
                  <Link
                    to={doctorLensHref(c.patientId, 'search')}
                    className="ac-btn ac-btn--primary ac-btn--sm"
                    data-testid="doctor-search-open"
                  >
                    View details
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * The STATEFUL doctor patient search tool -- embeddable (no PageHeader). Manages
 * query / loading / error / result and opens the lens directly on an exact
 * single match (the B0 flow). Consumed by DoctorPatientsPage as the demoted
 * secondary tool beneath the clinic patient list.
 */
export function DoctorPatientSearchTool(): JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DoctorPatientSearchResult | null>(null);

  const submit = (): void => {
    const q = query.trim();
    if (q.length < 2 || loading) return;
    setLoading(true);
    setError(null);
    fetchDoctorPatientSearch(q)
      .then((res) => {
        setLoading(false);
        // Exactly one lawful match -> open the lens directly (the B0 flow).
        if (!res.contactSearchWithheld && res.count === 1 && res.candidates[0]) {
          navigate(doctorLensHref(res.candidates[0].patientId, 'search'));
          return;
        }
        setResult(res);
      })
      .catch((err: unknown) => {
        setLoading(false);
        setResult(null);
        setError(err instanceof Error ? err.message : 'Search failed');
      });
  };

  return (
    <DoctorSearchBody
      query={query}
      onQueryChange={setQuery}
      onSubmit={submit}
      loading={loading}
      error={error}
      result={result}
    />
  );
}

/** The standalone search page shape (retained for reference / the named export
 *  test entry; the doctor Patients page mounts DoctorPatientsPage which leads
 *  with the clinic list and embeds DoctorPatientSearchTool below it). */
export default function DoctorPatientSearchPage(): JSX.Element {
  return (
    <div className="doctor-portal">
      <PageHeader
        title="Patients"
        subs={['Find any patient in your clinic to review care and continue treatment.']}
      />
      <DoctorPatientSearchTool />
    </div>
  );
}
